import { useEffect, useState } from 'react';
import { Badge, Box, Group, Menu, Popover, ScrollArea, Stack, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconChevronDown, IconHeadset } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import {
  BUSY_PRESENCES,
  OmniPresence,
  PRESENCE_META,
  USER_PRESENCE_STATUS,
  useGetMeQuery,
  useGetTaskOffersQuery,
  useMessagesStream,
  useOmniHeartbeat,
  useUpdatePresenceStatusMutation,
} from '@/store/slices/api/meApiSlice';
import { TaskResponseDto } from '@/types/task';
import { CATEGORY_COLOR, CATEGORY_LABEL } from '@/utils/taskConstants';
import routes from '@/routes/config';
import { TaskOfferCard } from './TaskOfferCard';

function AssignedTaskCard({ task, onNavigate }: { task: TaskResponseDto; onNavigate: () => void }) {
  const navigate = useNavigate();
  const dest = task.transmissionReportId
    ? routes.physician.transmissionReportDetail(task.transmissionReportId)
    : routes.physician.taskDetail(task.id);

  return (
    <Box
      onClick={() => { onNavigate(); navigate(dest); }}
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        background: '#fff',
        borderRadius: 10,
        padding: 10,
        cursor: 'pointer',
      }}>
      <Group justify="space-between" wrap="nowrap" mb={4} gap={6}>
        <Badge size="xs" radius="sm" variant="light" color={CATEGORY_COLOR[task.workType]}>
          {CATEGORY_LABEL[task.workType]}
        </Badge>
        <Text size="xs" c="plum.5" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {task.taskNumber}
        </Text>
      </Group>
      <Text size="xs" fw={700} c="plum.9" lh={1.3} truncate>
        {task.organization?.name ?? task.subject}
      </Text>
      {task.taskType && (
        <Text size="xs" c="dimmed" truncate>
          {task.taskType.name}
        </Text>
      )}
    </Box>
  );
}

interface OmniChannelWidgetProps {
  collapsed: boolean;
}

export function OmniChannelWidget({ collapsed }: OmniChannelWidgetProps) {
  const [open, setOpen] = useState(false);
  const { data: meData } = useGetMeQuery();
  const [presence, setPresence] = useState<OmniPresence>(USER_PRESENCE_STATUS.OFFLINE);
  const [updatePresenceStatus] = useUpdatePresenceStatusMutation();
  const { data: taskOffersData } = useGetTaskOffersQuery();
  const taskOffers = taskOffersData?.items ?? [];
  const assignedTasks = meData?.assignedTasks ?? [];

  const shouldBeOfferedTasks = meData?.shouldBeOfferedTasks ?? false;
  useMessagesStream(shouldBeOfferedTasks, () => setOpen(true), () => setOpen(true));
  useOmniHeartbeat(shouldBeOfferedTasks);

  useEffect(() => {
    if (meData?.presenceStatus) setPresence(meData.presenceStatus);
  }, [meData?.presenceStatus]);

  const handleSetPresence = async (p: OmniPresence) => {
    const prev = presence;
    setPresence(p);
    try {
      await updatePresenceStatus({ presenceStatus: p }).unwrap();
    } catch {
      setPresence(prev);
    }
  };

  const presenceMeta = PRESENCE_META[presence];

  return (
    <Popover
      opened={open}
      onChange={setOpen}
      position="right-end"
      offset={10}
      width={340}
      withinPortal
      radius={12}>
      <Popover.Target>
        <Tooltip label="Omni Channel" position="right" disabled={!collapsed} withArrow>
          <Box
            onClick={() => setOpen((o) => !o)}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              borderRadius: 8,
              padding: collapsed ? '10px 12px' : '10px 14px',
              cursor: 'pointer',
              color: open ? '#ffffff' : 'rgba(255,255,255,0.65)',
              backgroundColor: open ? 'rgba(255,255,255,0.12)' : 'transparent',
            }}>
            {/* Headset icon with presence dot */}
            <Box style={{ position: 'relative', flexShrink: 0, width: 20, height: 20 }}>
              <IconHeadset size={20} stroke={1.5} />
              <span style={{
                position: 'absolute',
                bottom: -4,
                right: -6,
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: presenceMeta.color,
                border: '2px solid #3b1a2e',
              }} />
            </Box>

            {!collapsed && (
              <>
                <Text size="sm" fw={500} c="inherit" style={{ flex: 1 }}>
                  Omni Channel
                </Text>
                {/* Alert badge: pending offers or assigned tasks */}
                {(taskOffers.length > 0 || assignedTasks.length > 0) && (
                  <Box style={{
                    minWidth: 18,
                    height: 18,
                    borderRadius: 999,
                    background: taskOffers.length > 0 ? '#e03131' : 'rgba(255,255,255,0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 5px',
                  }}>
                    <Text size="10px" fw={700} c="white" lh={1}>
                      {taskOffers.length + assignedTasks.length}
                    </Text>
                  </Box>
                )}
              </>
            )}

            {/* Collapsed: show red dot on icon when there are offers */}
            {collapsed && taskOffers.length > 0 && (
              <span style={{
                position: 'absolute',
                top: 6,
                right: 6,
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#e03131',
                border: '1.5px solid #3b1a2e',
              }} />
            )}
          </Box>
        </Tooltip>
      </Popover.Target>

      <Popover.Dropdown
        p={0}
        style={{
          background: 'var(--mantine-color-plum-0)',
          border: '1px solid var(--mantine-color-plum-4)',
          borderTop: '3px solid var(--mantine-color-plum-7)',
          boxShadow: '0 14px 40px rgba(33, 16, 24, 0.28)',
          maxHeight: '78vh',
          overflowY: 'auto',
        }}>

        {/* Header */}
        <Group
          justify="space-between"
          px="md"
          py="sm"
          style={{
            borderBottom: '1px solid var(--mantine-color-gray-2)',
            position: 'sticky',
            top: 0,
            background: '#fff',
            zIndex: 1,
          }}>
          <Group gap={8}>
            <IconHeadset size={17} stroke={1.7} color="var(--mantine-color-plum-7)" />
            <Text fw={700} size="sm" c="plum.9">
              Omni-Channel
            </Text>
          </Group>

          {/* Presence picker */}
          <Menu position="bottom-end" radius={10} withinPortal shadow="md">
            <Menu.Target>
              <Box
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  border: '1px solid var(--mantine-color-gray-3)',
                  borderRadius: 999,
                  padding: '3px 8px',
                }}>
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    background: presenceMeta.color,
                    flexShrink: 0,
                  }}
                />
                <Text size="xs" fw={600} c="plum.8">
                  {presenceMeta.label}
                </Text>
                <IconChevronDown size={12} color="var(--mantine-color-plum-5)" />
              </Box>
            </Menu.Target>
            <Menu.Dropdown p={4}>
              <ScrollArea.Autosize mah={320} type="auto">
                <Menu.Item
                  onClick={() => handleSetPresence(USER_PRESENCE_STATUS.AVAILABLE)}
                  leftSection={
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: PRESENCE_META.available.color }} />
                  }
                  rightSection={presence === USER_PRESENCE_STATUS.AVAILABLE ? <IconCheck size={14} /> : null}>
                  {PRESENCE_META.available.label}
                </Menu.Item>
                <Menu.Label c="dimmed" mt={4}>
                  Busy — no new routes
                </Menu.Label>
                {BUSY_PRESENCES.map((p) => (
                  <Menu.Item
                    key={p}
                    onClick={() => handleSetPresence(p)}
                    leftSection={
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: PRESENCE_META[p].color }} />
                    }
                    rightSection={presence === p ? <IconCheck size={14} /> : null}>
                    {PRESENCE_META[p].label}
                  </Menu.Item>
                ))}
                <Menu.Divider my={4} />
                <Menu.Item
                  onClick={() => handleSetPresence(USER_PRESENCE_STATUS.OFFLINE)}
                  leftSection={
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: PRESENCE_META.offline.color }} />
                  }
                  rightSection={presence === USER_PRESENCE_STATUS.OFFLINE ? <IconCheck size={14} /> : null}>
                  {PRESENCE_META.offline.label}
                </Menu.Item>
              </ScrollArea.Autosize>
            </Menu.Dropdown>
          </Menu>
        </Group>

        {/* Assigned to me */}
        {assignedTasks.length > 0 && (
          <Box px="md" pt="sm" pb="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
            <Text size="10px" fw={700} tt="uppercase" c="plum.5" mb={6} style={{ letterSpacing: '0.05em' }}>
              Assigned to me
            </Text>
            <Stack gap={6}>
              {assignedTasks.map((task) => (
                <AssignedTaskCard key={task.id} task={task} onNavigate={() => setOpen(false)} />
              ))}
            </Stack>
          </Box>
        )}

        {/* Queue */}
        <Box px="md" pt="sm" pb="md">
          <Text size="10px" fw={700} tt="uppercase" c="plum.5" mb={6} style={{ letterSpacing: '0.05em' }}>
            Available work
          </Text>
          {presence !== USER_PRESENCE_STATUS.AVAILABLE ? (
            <Text size="xs" c="plum.5" py={8}>
              {presence === USER_PRESENCE_STATUS.OFFLINE
                ? 'You are offline — not receiving routes. Set Available to receive routed calls or tasks.'
                : `Task routing paused (${presenceMeta.label}) — set Available to receive offers.`}
            </Text>
          ) : taskOffers.length === 0 ? (
            <Text size="xs" c="plum.5" py={8}>
              No tasks waiting. You're all caught up.
            </Text>
          ) : (
            <Stack gap={8}>
              {taskOffers.map((offer) => (
                <TaskOfferCard key={offer.id} offer={offer} />
              ))}
            </Stack>
          )}
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
}
